/** Song clips (no music video): pick official lyric lines (click the first line, then the last), see the window
 * (first word − lead through last word + hold; the cut appends the fade) and the length rule live, create the clip,
 * then choose its template. Below: this song's clips (song clips and the MV clips of its videos). */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { fmtMs } from "../../lib/frames";
import { useAction } from "../lib/actions";
import { ActionError, Empty, QueryView, Section, TableScroll } from "../lib/ui";

type Lines = Schemas["SongClipLines"];
type Clip = Schemas["ClipOut"];

export type Pick = { from: number | null; to: number | null };
export type Verdict = { tone: "ok" | "warn" | "bad"; text: string; allowed: boolean };

/** The window of a pick on the song clock (the same rule the API applies), or null while incomplete/untimed. */
export function songWindow(l: Lines, pick: Pick): { inMs: number; outMs: number } | null {
  if (pick.from == null || pick.to == null) return null;
  const a = l.lines.find((x) => x.idx === pick.from);
  const b = l.lines.find((x) => x.idx === pick.to);
  if (a?.t0_song_ms == null || b?.t1_song_ms == null) return null;
  const inMs = Math.max(0, a.t0_song_ms - l.lead_ms);
  let outMs = b.t1_song_ms + l.hold_ms;
  if (l.duration_ms) outMs = Math.min(outMs, l.duration_ms);
  return outMs > inMs ? { inMs, outMs } : null;
}

/** The length rule for a hand-made song clip: min–max is fine, manual_min–manual_max is allowed with a warning. */
export function lengthVerdict(l: Lines, durMs: number): Verdict {
  const s = (ms: number) => `${ms / 1000}`;
  if (durMs >= l.min_ms && durMs <= l.max_ms) return { tone: "ok", text: `within ${s(l.min_ms)}–${s(l.max_ms)} s`, allowed: true };
  if (durMs >= l.manual_min_ms && durMs <= l.manual_max_ms) {
    return { tone: "warn", text: `outside the usual ${s(l.min_ms)}–${s(l.max_ms)} s (allowed by hand, with a warning)`, allowed: true };
  }
  return { tone: "bad", text: `${durMs < l.manual_min_ms ? "too short" : "too long"}: song clips are ${s(l.manual_min_ms)}–${s(l.manual_max_ms)} s at most`, allowed: false };
}

/** Next pick after clicking line `idx`: the first click starts a range, the second ends it (either order), a third
 * starts over. */
export function nextPick(p: Pick, idx: number): Pick {
  if (p.from == null || p.to !== p.from) return { from: idx, to: idx };
  return idx >= p.from ? { from: p.from, to: idx } : { from: idx, to: p.from };
}

export function SongClipsSection({ songId, raw }: { songId: number; raw: boolean }) {
  const lines = useQuery({
    queryKey: qk.entity("song", songId, "clip-lines"),
    queryFn: () => unwrap(api.GET("/api/songs/{song_id}/clip-lines", { params: { path: { song_id: songId } } })),
  });
  const clips = useQuery({
    queryKey: qk.entity("song", songId, "clips"),
    queryFn: () => unwrap(api.GET("/api/songs/{song_id}/clips", { params: { path: { song_id: songId } } })),
  });
  return (
    <Section id="song-clips" title="Make a song clip">
      <p className="ws7-muted">
        No music video needed: pick official lyric lines and the clip plays the studio audio under the song's cover.
        Click the first line, then the last line. Only the synthetic (mg) and spin templates take a song clip.
      </p>
      <QueryView q={lines} what="lyric lines">
        {(l) => <Picker l={l} songId={songId} raw={raw} />}
      </QueryView>
      <h3>This song's clips</h3>
      <QueryView q={clips} what="clips">
        {(cs) => <ClipList clips={cs} />}
      </QueryView>
    </Section>
  );
}

function Picker({ l, songId, raw }: { l: Lines; songId: number; raw: boolean }) {
  const [pick, setPick] = useState<Pick>({ from: null, to: null });
  const [label, setLabel] = useState("");
  const [made, setMade] = useState<Clip | null>(null);
  const win = useMemo(() => songWindow(l, pick), [l, pick]);
  const dur = win ? win.outMs - win.inMs : null;
  const verdict = dur != null ? lengthVerdict(l, dur) : null;
  const create = useAction(
    (p: { line_from: number; line_to: number; label: string | null }) =>
      unwrap(api.POST("/api/songs/{song_id}/clips", { params: { path: { song_id: songId } }, body: p })),
    {
      label: "Create song clip",
      success: (c) => `Song clip #${c.id} created (${fmtMs(c.t_song_in_ms)}–${fmtMs(c.t_song_out_ms)}); it is being cut`,
      invalidate: [qk.entity("song", songId, "clips")],
    },
  );
  const ready = l.problems.length === 0;
  const canCreate = ready && win != null && verdict?.allowed === true && !create.pending;
  const submit = async () => {
    if (pick.from == null || pick.to == null) return;
    const c = await create.run({ line_from: pick.from, line_to: pick.to, label: label.trim() || null });
    if (c) {
      setMade(c);
      setPick({ from: null, to: null });
      setLabel("");
    }
  };
  const inRange = (idx: number) => pick.from != null && pick.to != null && idx >= pick.from && idx <= pick.to;
  return (
    <>
      {!ready && (
        <ul className="ws7-tone-warn" data-testid="song-clip-problems">
          {l.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {l.lines.length === 0 ? <Empty>No official lyric lines yet.</Empty> : (
        <TableScroll label="Official lyric lines" className="ws7-scroll ws7-lines-pick">
          <ol className="ws7-line-pick" aria-label="Official lyric lines">
            {l.lines.map((x) => {
              const timed = x.t0_song_ms != null;
              const text = (raw ? x.text_raw : x.text_masked) || x.text_raw;
              return (
                <li key={x.idx}>
                  <button type="button" data-testid={`song-clip-line-${x.idx}`} disabled={!timed || !ready}
                    aria-pressed={inRange(x.idx)} className={inRange(x.idx) ? "ws7-line-on" : undefined}
                    aria-label={`Line ${x.idx}${timed ? ` at ${fmtMs(x.t0_song_ms)}` : " (no timed words)"}: ${text}`}
                    onClick={() => setPick((p) => nextPick(p, x.idx))}>
                    <span className="ws7-mono">{timed ? fmtMs(x.t0_song_ms) : "–"}</span> {text}
                  </button>
                </li>
              );
            })}
          </ol>
        </TableScroll>
      )}
      <p data-testid="song-clip-duration" className={verdict ? `ws7-tone-${verdict.tone}` : "ws7-muted"} aria-live="polite">
        {win && dur != null && verdict
          ? <>Lines {pick.from}–{pick.to}: window {fmtMs(win.inMs)}–{fmtMs(win.outMs)} · <strong>{(dur / 1000).toFixed(2)} s</strong>
            {" "}(+ {l.fade_ms / 1000} s fade) · {verdict.text}</>
          : pick.from != null ? "Now click the last line." : "Pick the first line."}
      </p>
      <div className="ws7-toolbar">
        <label className="ws7-field">Label (optional)
          <input value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} data-testid="song-clip-label" />
        </label>
        <button type="button" className="ws7-primary" data-testid="song-clip-create" disabled={!canCreate} onClick={() => void submit()}>
          Create song clip
        </button>
        {pick.from != null && (
          <button type="button" data-testid="song-clip-clear" onClick={() => setPick({ from: null, to: null })}>Clear</button>
        )}
      </div>
      <ActionError error={create.error} />
      {made && (
        <p className="ws7-tone-ok" data-testid="song-clip-made">
          Song clip #{made.id} created ({(made.dur_ms / 1000).toFixed(2)} s with the fade)
          {made.warnings.length ? ` · ${made.warnings.join("; ")}` : ""}.{" "}
          <Link to={`/choose?clip=${made.id}`} data-testid="song-clip-choose">Choose a template</Link>
          {" · "}<Link to={`/clips/${made.id}`}>Clip page</Link>
        </p>
      )}
    </>
  );
}

function ClipList({ clips }: { clips: Clip[] }) {
  if (clips.length === 0) return <Empty>No clips of this song yet.</Empty>;
  return (
    <TableScroll label="This song's clips">
      <table className="ws7-table" aria-label="This song's clips" data-testid="song-clips-list">
        <thead><tr><th scope="col">Clip</th><th scope="col">Kind</th><th scope="col">Window</th><th scope="col">Length</th>
          <th scope="col">Template</th><th scope="col">Next</th></tr></thead>
        <tbody>
          {clips.map((c) => (
            <tr key={c.id} data-testid={`song-clip-row-${c.id}`}>
              <td><Link to={`/clips/${c.id}`}>#{c.id}{c.label ? ` ${c.label}` : ""}</Link></td>
              <td>{c.kind === "song" ? "song clip" : "MV clip"}</td>
              <td>{c.kind === "song"
                ? `song ${fmtMs(c.t_song_in_ms)}–${fmtMs(c.t_song_out_ms)}`
                : <>MV {fmtMs(c.t_mv_in_ms)}–{fmtMs(c.t_mv_out_ms)}{c.video_id != null && <> · <Link to={`/watch/${c.video_id}`}>video #{c.video_id}</Link></>}</>}
              </td>
              <td className="num">{(c.dur_ms / 1000).toFixed(1)} s</td>
              <td>{c.template_state.replace("_", " ")}{c.master_media_id == null ? " · not cut yet" : ""}</td>
              <td>{c.template_state === "needs_template" ? <Link to={`/choose?clip=${c.id}`}>Choose a template</Link> : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
